<script>
  import { Spinner } from '@client/components/spinner';

  /**
   * Array of items to display in the selectable list.
   * @type {Array}
   * @default []
   */
  export let items = [];

  /**
   * ID of the currently selected item.
   * @type {string | null}
   * @default null
   */
  export let selectedId = null;

  /**
   * Callback function invoked when an item is selected.
   * @type {Function}
   * @default () => {}
   */
  export let onSelect = () => {};

  /**
   * Message displayed when no items are available.
   * @type {string}
   * @default 'No items found'
   */
  export let emptyMessage = 'No items found';

  /**
   * Additional CSS class applied to each item.
   * @type {string}
   * @default ''
   */
  export let itemClass = '';

  /**
   * Controls whether the loading spinner is displayed.
   * @type {boolean}
   * @default false
   */
  export let loading = false;
</script>

<div class="selectable-list">
  {#if loading}
    <div class="empty">
      <Spinner show inline />
    </div>
  {:else if items.length === 0}
    <div class="empty">
      <p>{emptyMessage}</p>
    </div>
  {:else}
    {#each items as item (item._id || item.name)}
      <div
        class="selectable-item {itemClass}"
        class:selected={selectedId === (item._id || item.name)}
        on:click={() => onSelect(item._id || item.name)}
        on:keydown={(e) => e.key === 'Enter' && onSelect(item._id || item.name)}
        role="button"
        tabindex="0"
      >
        <slot {item} selected={selectedId === (item._id || item.name)} />
      </div>
    {/each}
  {/if}
</div>

<style lang="scss">
  .selectable-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: var(--padding-small);

    .empty {
      text-align: center;
      padding: var(--padding-xlarge);
      color: var(--color-gray-600);
    }

    .selectable-item {
      background: var(--color-white);
      border: 1px solid var(--border-color);
      border-radius: var(--border-radius-large);
      cursor: pointer;
      padding: var(--padding-large);
      transition: all 0.2s ease;

      &:hover {
        box-shadow: 0 0 0 4px var(--color-gray-100);
      }

      &.selected {
        background-color: var(--color-blue-50);
        border-color: var(--color-blue-400);
      }

      &:focus {
        border-color: var(--color-blue-400);
        box-shadow: 0 0 0 3px var(--color-blue-400);
        outline: none;
      }
    }
  }
</style>
